import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronUp, Search, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useConfirm } from '../components/ui/confirm';
import CeldaSemaforo from '../components/CeldaSemaforo';
import BajasPedidas from '../components/BajasPedidas';
import DarDeAlta from '../components/DarDeAlta';
import { useAuth } from '../context/AuthContext';
import { accionDeEntrenador, avisoAlAsignarmelo } from '../lib/asignarEntrenador';
import { escribirFiltros } from '../lib/filtrosEnLaDireccion';
import {
    FILTROS_DE_CLIENTES, MOMENTOS, enElMomento, esDeLaLista, filtrosDeClientesAlEntrar,
} from '../lib/filtrosDeClientes';
import {
    CELDAS_DEL_SEMAFORO, CLAVES_DE_ORDEN, ESTADO_MACROS, SIN_FECHA, cuantosMiembros, enlaceDeLaFicha, filasDelGestor,
    nombreDeEntrenador, pastillasDePlan,
} from '../lib/gestorDeClientes';
import { hoyLocal } from '../lib/horaEspana';
import { mensajeDeError } from '../lib/mensajeDeError';

/**
 * LA PANTALLA CLIENTES (tarea 10 de la hoja del 15-10; lienzo «D · La Calma con nuestro
 * diseño» del 19-10). Desde el 19-10 sustituye a la lista vieja (Francisco: «este rediseño
 * reemplaza a la pestaña de clientes que ya existía»), y lo que solo hacía aquella se trajo
 * aquí: quedarte un cliente, el
 * filtro de momento, dar de alta y los enlaces del Dashboard (`?orden=sin_tocar`). Salen
 * SOLO LOS ACTIVOS, sin pestañas (Francisco, 19-10: `lib/filtrosDeClientes.esDeLaLista`).
 *
 * Desde el 21-10 es una tabla (solo ordenador): FICHA, ÚLT. MENSUAL
 * (la fecha de su último mensual en naranja, abre su Seguimiento), MACROS («macros» en verde
 * o «cambiar macros» en rojo, abre sus Macros), NOMBRE, EMAIL y PLAN. El nombre despliega su
 * semana, su ciclo, el último macro y los cobros. La regla de los colores está en el
 * servidor (`core/macros_tras_el_mensual`); los filtros, en `lib/filtrosDeClientes`, y el
 * orden y lo que se escribe, en `lib/gestorDeClientes`.
 *
 * FONDO BLANCO (vídeo de Jesús y decisión de Francisco, 20-10): solo el contenido de esta
 * pantalla; el menú lateral y el resto del panel siguen oscuros.
 */
const PASTILLA = 'inline-flex items-center rounded-lg font-bold whitespace-nowrap min-h-[36px] sm:min-h-[24px] px-2.5 text-[13px] sm:text-[11px]';
// Los tonos de `estadoDeAcceso` y de «Renueva», oscurecidos para que se lean sobre blanco.
const TONO = { ok: 'text-[#15803D]', aviso: 'text-[#B45309]', apagado: 'text-[#57544E]' };
// Los campos (buscador y desplegables) sobre blanco.
const CAMPO = 'bg-white border-[#D6D2C9] text-[#1C1B19]';
const OPCION = 'text-[#1C1B19] focus:bg-[#F6F5F2] focus:text-[#1C1B19]';




// LA TABLA (21-10): las columnas que pidió el equipo, en este orden. Solo ordenador: el
// panel del equipo no se usa en el móvil. Las casillas para marcar varios (asignar tarea,
// desactivar) se quitaron el 21-10.
// Cada una con la clave por la que ordena su flecha (`lib/gestorDeClientes`).
const COLUMNAS = [
    ['ficha', 'FICHA'], ['reporte', 'ÚLT. MENSUAL'], ['macros', 'MACROS'],
    ['nombre', 'NOMBRE'], ['email', 'EMAIL'], ['plan', 'PLAN'], ['entrenador', 'ENTRENADOR'],
    ['ciclo', 'SEMANA CICLO'], ['rutina', 'SEMANA RUTINA'], ['repite', 'REPITE SEMANA'],
];

/** El título de una columna con sus dos flechas: arriba, de menor a mayor; abajo, de mayor
 *  a menor. La que está puesta va en naranja; pulsarla otra vez vuelve al orden de siempre. */
const Titulo = ({ clave, texto, orden, sentido, onOrdenar }) => {
    const flecha = (dir, Icono, ayuda) => {
        const puesta = orden === clave && sentido === dir;
        return (
            <button type="button" onClick={() => onOrdenar(puesta ? null : clave, dir)}
                aria-pressed={puesta} aria-label={`${texto}: ${ayuda}`} title={ayuda}
                data-testid={`ordenar-${clave}-${dir}`}
                className={`leading-none ${puesta ? 'text-[#FF671F]' : 'text-[#B5B0A6] hover:text-[#57544E]'}`}>
                <Icono className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
        );
    };
    return (
        <span className="inline-flex items-center gap-1.5">
            {texto}
            <span className="inline-flex flex-col -my-1">
                {flecha('asc', ChevronUp, 'de menor a mayor')}
                {flecha('desc', ChevronDown, 'de mayor a menor')}
            </span>
        </span>
    );
};
const CELDA = 'px-3 py-2 align-middle';
// LA TABLA, MÁS ESTRECHA (21-10): el nombre y el plan parten en dos líneas dentro de su
// ancho en vez de estirar la tabla hacia la derecha.
const ANCHO_NOMBRE = 'w-[11rem] max-w-[11rem]';
const ANCHO_PLAN = 'w-[8rem] max-w-[8rem] break-words leading-snug';

// GESTIÓN DE SUSCRIPCIONES (21-10): la segunda pestaña. Los pagos son los cobros reales
// (Holded, ThriveCart y Stripe); la baja, el día en que pidió «No quiero renovar».
const COLUMNAS_DE_SUSCRIPCIONES = [
    ['ficha', 'FICHA'], ['nombre', 'NOMBRE'], ['email', 'EMAIL'], ['plan', 'PLAN'],
    ['primer_pago', 'PRIMER PAGO'], ['ultimo_pago', 'ÚLTIMO PAGO'], ['baja', 'SOLICITUD DE BAJA'],
];
// Los dos botones de arriba de la pestaña: atajos a un orden de la tabla, el más reciente arriba.
const ATAJOS_DE_SUSCRIPCIONES = [
    ['ultimo_pago', 'Pagos más recientes'],
    ['baja', 'Bajas más recientes'],
];

const FilaDeSuscripcion = ({ f }) => (
    <tr className="border-b border-[#E5E2DB] last:border-0 hover:bg-[#F6F5F2]" data-testid={`suscripcion-fila-${f.clave}`}>
        <td className={CELDA}>
            {f.id ? (
                <Link to={enlaceDeLaFicha(f.id)}
                    className={`${PASTILLA} bg-[#EFECE6] text-[#1C1B19] font-semibold hover:bg-[#E5E2DB]`}>
                    Ficha
                </Link>
            ) : (
                <span className={`${PASTILLA} bg-[#EFECE6] text-[#57544E] font-semibold`}>Sin ficha</span>
            )}
        </td>
        <td className={`${CELDA} ${ANCHO_NOMBRE} break-words font-bold ${f.seVa ? 'text-[#B91C1C]' : 'text-[#1C1B19]'}`}>{f.nombre}</td>
        <td className={`${CELDA} text-[#57544E] break-all`}>{f.email}</td>
        <td className={`${CELDA} ${ANCHO_PLAN} text-[#1C1B19]`}>{f.plan}</td>
        <td className={`${CELDA} text-[#1C1B19] whitespace-nowrap tabular-nums`}>{f.primerPago}</td>
        <td className={`${CELDA} text-[#1C1B19] whitespace-nowrap tabular-nums`}>{f.ultimoPago}</td>
        <td className={`${CELDA} whitespace-nowrap tabular-nums text-[#B91C1C]`}
            title={f.bajaPedida === SIN_FECHA ? 'Tiene la marca de no renovar, pero no consta cuándo la pidió' : undefined}>
            {f.bajaPedida}
        </td>
    </tr>
);

const Fila = ({ f, abierto, onToggle, entrenador, puedeQuedarselo, onQuedarselo }) => (
    <>
    <tr className="border-b border-[#E5E2DB] last:border-0 hover:bg-[#F6F5F2]" data-testid={`gestor-fila-${f.clave}`}>
        <td className={CELDA}>
            {f.id ? (
                <Link to={enlaceDeLaFicha(f.id)} data-testid={`abrir-ficha-${f.id}`}
                    className={`${PASTILLA} bg-[#EFECE6] text-[#1C1B19] font-semibold hover:bg-[#E5E2DB]`}>
                    Ficha
                </Link>
            ) : (
                <span className={`${PASTILLA} bg-[#EFECE6] text-[#57544E] font-semibold`}
                    title="Se registró y no eligió plan: todavía no tiene ficha">Sin ficha</span>
            )}
        </td>
        {/* ÚLT. MENSUAL: siempre la fecha del último mensual, se ordene por lo que se ordene. */}
        <td className={CELDA}>
            {f.mensual && (f.id ? (
                <Link to={enlaceDeLaFicha(f.id, 'seguimiento')} title="Abrir sus Reportes"
                    data-testid={`gestor-mensual-${f.id}`}
                    className={`${PASTILLA} bg-[#FDEBDF] text-[#B43E06] tabular-nums hover:bg-[#FBD9C3]`}>
                    {f.mensual}
                </Link>
            ) : (
                <span className={`${PASTILLA} bg-[#FDEBDF] text-[#B43E06] tabular-nums`}>{f.mensual}</span>
            ))}
        </td>
        <td className={CELDA}>
            {f.estado === ESTADO_MACROS.HECHOS && (
                <span className={`${PASTILLA} bg-[#DCFCE7] text-[#15803D]`}
                    data-testid={`gestor-macros-${f.id}`}>
                    macros
                </span>
            )}
            {f.estado === ESTADO_MACROS.CAMBIAR && f.id && !f.fuera && (
                <Link to={enlaceDeLaFicha(f.id, 'macros')} title="Abrir sus Macros"
                    data-testid={`gestor-cambiar-${f.id}`}
                    className={`${PASTILLA} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}>
                    cambiar macros
                </Link>
            )}
        </td>
        <td className={`${CELDA} ${ANCHO_NOMBRE}`}>
            <span className="inline-flex flex-wrap items-center gap-1.5">
                <button type="button" onClick={onToggle} aria-expanded={abierto}
                    title={f.seVa ? 'Pidió la baja: no se le volverá a cobrar' : undefined}
                    className={`text-left font-bold break-words min-w-0 ${f.seVa ? 'text-[#B91C1C] hover:text-[#991B1B]' : 'text-[#1C1B19] hover:text-black'}`}>
                    {f.nombre}
                </button>
                {/* Punto 39: la excepción se ve sin abrir la ficha. */}
                {f.excepcion && (
                    <span title={f.excepcion} data-testid={`excepcion-${f.id}`}
                        className="text-[#C2410C] cursor-help inline-flex"><AlertTriangle className="w-3.5 h-3.5" /></span>
                )}
                {/* LA PAUSA (24-09): su semana no se mueve. */}
                {f.enPausa && (
                    <span className={`${PASTILLA} bg-[#DBEAFE] text-[#1E3A8A] w-fit`} data-testid={`en-pausa-${f.id}`}>En pausa</span>
                )}
            </span>
        </td>
        <td className={`${CELDA} text-[#57544E] break-all`}>{f.email}</td>
        <td className={`${CELDA} ${ANCHO_PLAN} text-[#1C1B19]`}>{f.plan}</td>
        {/* ENTRENADOR (hoja del 22-10, fila 75). Con `trainer_id` y sin nombre en la lista del
            equipo sí tiene entrenador: «asignado», no vacío (lo mismo que el desplegable). */}
        <td className={`${CELDA} text-[#1C1B19]`} data-testid={`entrenador-${f.clave}`}>
            {f.entrenador || (f.entrenadorId ? <span className="text-[#57544E]">asignado</span> : <span className="text-[#57544E]">sin entrenador</span>)}
        </td>
        {/* Sin contador (sin semana o sin rutina cargada en la app), la celda va vacía. */}
        <td className={`${CELDA} text-[#1C1B19] whitespace-nowrap tabular-nums`}
            data-testid={`semana-ciclo-${f.clave}`}>{f.semanaCiclo}</td>
        <td className={`${CELDA} text-[#1C1B19] whitespace-nowrap tabular-nums`}
            data-testid={`semana-de-rutina-${f.clave}`}>{f.semanaRutina}</td>
        {/* Se quedó en la semana del mensual por no mandarlo: cuántas lleva. Vacía si no repite. */}
        <td className={`${CELDA} whitespace-nowrap tabular-nums text-[#B43E06] font-semibold`}
            title={f.repite ? 'Sigue en la semana del reporte mensual porque no lo ha mandado' : undefined}
            data-testid={`repite-semana-${f.clave}`}>{f.repite}</td>
    </tr>
        {abierto && (
            <tr><td colSpan={COLUMNAS.length} className="px-3 pb-3 pt-0">
            <div className="rounded-xl bg-[#F6F5F2] border border-[#E5E2DB] px-3 py-2.5 flex flex-col gap-0.5 text-[13px] leading-[19px] text-[#57544E]"
                data-testid={`gestor-detalle-${f.clave}`}>
                <span className="text-[#1C1B19] font-semibold">
                    {f.semana}{f.planVencido ? ' · Plan vencido' : ''}
                    <span className="text-[#57544E] font-normal" data-testid={`semana-rutina-${f.clave}`}> · {f.rutina}</span>
                </span>
                {/* El estado con palabras, el mismo que ve el cliente al entrar (#64). */}
                <span data-testid={`estado-${f.clave}`}>
                    Estado: <span className={TONO[f.acceso.tono] || 'text-[#57544E]'}>{f.acceso.texto}</span>
                </span>
                {f.excepcion && <span className="text-[#B43E06]">Excepción: {f.excepcion}</span>}
                <span>Empezó el ciclo: {f.empezo}</span>
                <span>Último macro: {f.ultimoMacro}</span>
                <span>Último reporte mensual: {f.mensualLargo}</span>
                {f.debe && <span className="text-[#B43E06]" data-testid={`sin-reporte-${f.clave}`}>{f.debe}</span>}
                <span>Próximo cobro: {f.proximoPago}</span>
                <span>Membresía en vigor: {f.plan} hasta {f.hasta}</span>
                {f.id && (
                    <span data-testid={`renueva-${f.clave}`}>
                        Renueva: <span className={TONO[f.renueva.tono]} title={f.renueva.ayuda}>{f.renueva.texto}</span>
                        {f.precio && <> · Paga {f.precio}</>}
                    </span>
                )}
                {f.seVa && <span className="text-[#B91C1C]">Pidió la baja: no se le volverá a cobrar.</span>}
                <span>Última entrada en la app: {f.ultimaEntrada}</span>
                {f.sinCerrar && (
                    <span data-testid={`sin-cerrar-${f.clave}`}>
                        Días sin cerrar: <span className={f.sinCerrar.alerta ? 'text-[#B43E06] font-semibold' : ''}>{f.sinCerrar.texto}</span>
                    </span>
                )}
                {/* EL SEMÁFORO (punto 32): el color y el texto los calcula el servidor contra
                    el plazo del plan de cada cliente. */}
                {f.id && (
                    <span className="flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`semaforo-${f.clave}`}>
                        {CELDAS_DEL_SEMAFORO.map(([clave, etiqueta]) => (
                            <span key={clave} className="whitespace-nowrap">
                                {etiqueta}: <CeldaSemaforo celda={f.semaforo[clave]} testId={`semaforo-${clave}-${f.clave}`} tamano="text-[13px]" claro />
                            </span>
                        ))}
                    </span>
                )}
                <span className="flex flex-wrap items-center gap-2">
                    {/* Con `trainer_id` y sin nombre (borrado, o la lista del equipo no llegó) sí
                        tiene entrenador: no se dice «sin entrenador» (revisor, 19-10). */}
                    Entrenador: {entrenador || (f.entrenadorId ? 'asignado (no sale en la lista del equipo)' : 'sin entrenador')}
                    {puedeQuedarselo && (
                        <button type="button" onClick={onQuedarselo} data-testid={`assign-me-${f.id}`}
                            className="text-[#C2410C] font-semibold hover:underline">Quedármelo</button>
                    )}
                </span>
            </div>
            </td></tr>
        )}
    </>
);

export default function GestorDeClientesPage() {
    const { api, user, planCatalog } = useAuth();
    const { confirm } = useConfirm();
    const navigate = useNavigate();
    const esAdmin = user?.role === 'admin';
    // LOS FILTROS VIVEN EN LA DIRECCIÓN (14-10): al volver de una ficha sale como se dejó.
    const [filtros, setFiltros] = useState(
        () => filtrosDeClientesAlEntrar(window.location.search, CLAVES_DE_ORDEN));
    const poner = (clave) => (valor) => setFiltros((f) => ({ ...f, [clave]: valor }));
    const { q, plan, momento, orden, sentido, vista } = filtros;
    // Cambiar de pestaña vuelve al orden de siempre: cada una ordena por sus columnas.
    const ponerVista = (v) => setFiltros((f) => ({
        ...f, vista: v, orden: FILTROS_DE_CLIENTES.orden, sentido: FILTROS_DE_CLIENTES.sentido }));
    const deSuscripciones = vista === 'suscripciones';
    const columnas = deSuscripciones ? COLUMNAS_DE_SUSCRIPCIONES : COLUMNAS;
    // Las flechas de los títulos (21-10). Sin columna, el orden de siempre («Reporte mensual»:
    // arriba los que esperan macros).
    const ordenar = (clave, dir) => setFiltros((f) => (clave
        ? { ...f, orden: clave, sentido: dir }
        : { ...f, orden: FILTROS_DE_CLIENTES.orden, sentido: FILTROS_DE_CLIENTES.sentido }));
    useEffect(() => {
        const nueva = escribirFiltros(window.location.search, { ...filtros, q: q.trim() }, FILTROS_DE_CLIENTES);
        if (nueva !== window.location.search) navigate({ search: nueva }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cuando cambia un filtro
    }, [filtros]);

    const [clientes, setClientes] = useState(null);
    const [entrenadores, setEntrenadores] = useState([]);
    const [error, setError] = useState(false);
    const [abiertos, setAbiertos] = useState({});
    const busqueda = useDeferredValue(q);

    // Sin los registros a medias: solo salen los activos (19-10).
    const cargar = useCallback(() => {
        api.get('/admin/clients')
            .then((r) => { setClientes(r.data || []); setError(false); })
            .catch((e) => { console.error('[clientes]', e); setError(true); });
    }, [api]);
    useEffect(() => { cargar(); }, [cargar]);
    // «Han pedido la baja (últimos 10 días)», solo al abrir Gestión de suscripciones (21-10).
    const [bajas, setBajas] = useState(null);
    useEffect(() => {
        if (!deSuscripciones || bajas) return;
        api.get('/admin/bajas-pedidas').then((r) => setBajas(r.data?.bajas_pedidas || []))
            .catch((e) => console.error('[clientes] bajas pedidas', e));
    }, [api, deSuscripciones, bajas]);
    useEffect(() => {
        api.get('/admin/trainers').then((r) => setEntrenadores(r.data || []))
            .catch((e) => console.error('[clientes] entrenadores', e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lista = clientes || [];
    // SOLO LOS ACTIVOS (Francisco, 19-10): `lib/filtrosDeClientes.esDeLaLista`. Tampoco al
    // buscar: los que ya no están no salen nunca (21-10; quita los «Fuera» del 20-10).
    const activos = useMemo(() => lista.filter(esDeLaLista),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `lista` sale de `clientes`
        [clientes]);
    const visibles = useMemo(() => activos.filter((c) => enElMomento(c, momento)), [activos, momento]);
    const filas = useMemo(
        () => filasDelGestor(visibles, { buscado: busqueda, catalogo: planCatalog, hoy: hoyLocal(), plan, orden, sentido,
            entrenadores, primeroLosQueEsperan: !deSuscripciones }),
        [visibles, busqueda, planCatalog, plan, orden, sentido, entrenadores, deSuscripciones]);
    // El desplegable de plan cuenta solo activos, busque o no.
    const planes = useMemo(
        () => pastillasDePlan(activos.filter((c) => enElMomento(c, momento)), planCatalog),
        [activos, momento, planCatalog]);
    const alternar = (clave) => setAbiertos((a) => ({ ...a, [clave]: !a[clave] }));

    // El entrenador se asigna el cliente a sí mismo (12-10). Si ya lo lleva otro, se pregunta
    // antes diciendo quién. La regla la guarda el servidor.
    const quedarselo = async (f) => {
        const aviso = avisoAlAsignarmelo({
            cliente: f.nombre, entrenadorId: f.entrenadorId,
            entrenadorNombre: f.entrenadorId ? nombreDeEntrenador(entrenadores, f.entrenadorId) : null, miId: user?.id,
        });
        if (aviso && !await confirm(aviso)) return;
        try {
            await api.put(`/admin/clients/${f.id}/trainer`, { trainer_id: user.id });
            toast.success('Cliente asignado');
            cargar();
        } catch (err) {
            toast.error(mensajeDeError(err, 'No se pudo asignar el cliente'));
        }
    };

    // EL VACÍO DICE POR QUÉ ESTÁ VACÍO (ficha 108): qué filtros hay puestos, y se quitan de un
    // clic.
    const hayFiltros = plan !== 'all' || momento !== 'todos';
    const filtrosPuestos = [
        plan !== 'all' && `plan ${planes.find(([v]) => v === plan)?.[1] || plan}`,
        momento !== 'todos' && MOMENTOS.find(([v]) => v === momento)?.[1].toLowerCase(),
        q.trim() && `buscando «${q.trim()}»`,
    ].filter(Boolean);
    const quitarFiltros = () => setFiltros((f) => ({ ...f, plan: 'all', momento: 'todos', q: '' }));

    return (
        <div className="p-4 md:p-6 space-y-5 animate-fade-in bg-[#EFEDE8] [color-scheme:light] min-h-screen text-[#1C1B19]" data-testid="gestor-de-clientes">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-1 min-w-0">
                    <h1 className="m-0 font-heading text-2xl sm:text-[26px] font-bold uppercase">Clientes</h1>
                    <p className="m-0 text-sm text-[#57544E]" data-testid="clientes-total">
                        Número de miembros: <b className="tabular-nums text-[#B43E06]" data-testid="gestor-total">{clientes ? cuantosMiembros(filas) : '…'}</b>
                    </p>
                </div>
                {/* Solo el admin: el alta abre acceso y eso no lo hace un entrenador (25-09). */}
                {esAdmin && <DarDeAlta onCreado={cargar} />}
            </div>

            {/* LAS DOS PESTAÑAS (21-10). */}
            <div className="flex gap-1 border-b border-[#D6D2C9]" role="tablist">
                {[['clientes', 'Clientes'], ['suscripciones', 'Gestión de suscripciones']].map(([v, texto]) => (
                    <button key={v} type="button" role="tab" aria-selected={vista === v} onClick={() => ponerVista(v)}
                        data-testid={`pestana-${v}`}
                        className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${vista === v
                            ? 'border-[#FF671F] text-[#1C1B19]' : 'border-transparent text-[#57544E] hover:text-[#1C1B19]'}`}>
                        {texto}
                    </button>
                ))}
            </div>

            <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57544E]" aria-hidden="true" />
                    <Input type="search" value={q} onChange={(e) => poner('q')(e.target.value)}
                        placeholder="Nombre, apellidos o correo" aria-label="Buscar cliente" data-testid="client-search"
                        className={`pl-10 ${CAMPO} placeholder:text-[#8A867E] focus:border-[#FF671F]`} />
                </div>
                {/* EL PLAN, UN DESPLEGABLE (21-10): las pastillas ocupaban línea y media. Con su
                    número de clientes; el plan que viene en la dirección y ya no tiene a nadie
                    se añade con 0 para que no se pierda el filtro. */}
                <Select value={plan} onValueChange={poner('plan')}>
                    <SelectTrigger className={`w-full md:w-[220px] ${CAMPO}`} data-testid="filtro-plan"
                        aria-label="Plan">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={CAMPO}>
                        {(plan !== 'all' && !planes.some(([v]) => v === plan)
                            ? [...planes, [plan, planCatalog?.[plan]?.name || plan, 0]] : planes)
                            .map(([v, etiqueta, n]) => (
                                <SelectItem key={v} value={v} className={OPCION}>
                                    {v === 'all' ? 'Todos los planes' : etiqueta}{n != null ? ` (${n})` : ''}
                                </SelectItem>
                            ))}
                    </SelectContent>
                </Select>
                <Select value={momento} onValueChange={poner('momento')}>
                    <SelectTrigger className={`w-full md:w-[220px] ${CAMPO}`} data-testid="filtro-momento">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={CAMPO}>
                        {MOMENTOS.map(([v, etiqueta]) => <SelectItem key={v} value={v} className={OPCION}>{etiqueta}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>


            {deSuscripciones && bajas && <BajasPedidas bajas={bajas} catalogo={planCatalog} />}

            {deSuscripciones && (
                <div className="flex flex-wrap gap-2" role="group" aria-label="Ordenar suscripciones">
                    {ATAJOS_DE_SUSCRIPCIONES.map(([clave, texto]) => {
                        const puesto = orden === clave && sentido === 'desc';
                        return (
                            <button key={clave} type="button" aria-pressed={puesto} data-testid={`atajo-${clave}`}
                                onClick={() => ordenar(puesto ? null : clave, 'desc')}
                                className={`min-h-[36px] px-3.5 rounded-full text-[13px] font-semibold border transition-colors ${puesto
                                    ? 'bg-[#FF671F] border-[#FF671F] text-white'
                                    : 'bg-white border-[#D6D2C9] text-[#57544E] hover:text-[#1C1B19] hover:border-[#57544E]'}`}>
                                {texto}
                            </button>
                        );
                    })}
                </div>
            )}

            <Card className="bg-white border-[#E5E2DB] text-[#1C1B19] shadow-sm">
                <CardContent className="p-0 px-3.5 sm:px-[18px] py-1 sm:py-3.5">
                    {error && (
                        <p className="text-[#57544E] text-sm py-8 text-center">
                            No hemos podido cargar los clientes. Recarga la página.
                        </p>
                    )}
                    {!error && !clientes && (
                        <div className="p-8 text-center" role="status" aria-label="Cargando clientes">
                            <div className="animate-spin w-8 h-8 border-2 border-[#FF671F] border-t-transparent rounded-full mx-auto"></div>
                        </div>
                    )}
                    {!error && clientes && filas.length === 0 && (
                        <div className="p-8 text-center" data-testid="clientes-vacio">
                            <Users className="w-12 h-12 text-[#D6D2C9] mx-auto mb-4" aria-hidden="true" />
                            <p className="text-[#57544E]">
                                {q.trim() ? 'No hay clientes que se llamen así' : hayFiltros ? 'No hay clientes con ese filtro' : 'No hay clientes'}
                                {q.trim() && hayFiltros ? ' con estos filtros' : ''}
                            </p>
                            {(hayFiltros || q.trim()) && (
                                <>
                                    <p className="text-[#57544E] text-xs mt-2" data-testid="clientes-vacio-filtros">
                                        Estás mirando: {filtrosPuestos.join(' · ')}
                                    </p>
                                    <button type="button" onClick={quitarFiltros} data-testid="quitar-filtros"
                                        className="mt-4 text-xs font-bold uppercase tracking-wider text-[#B43E06] border border-[#FF671F]/50 hover:bg-[#FDEBDF] rounded-lg px-4 py-2 transition-colors">
                                        Quitar filtros
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                    {!error && filas.length > 0 && (
                        // La tabla tiene más columnas de las que caben en un móvil (y en un portátil
                        // con la barra lateral): se desliza dentro de la tarjeta, no se sale de ella.
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-sm" data-testid="tabla-de-clientes">
                                <thead>
                                    <tr className="border-b border-[#E5E2DB]">
                                        {columnas.map(([clave, texto]) => (
                                            <th key={clave} scope="col"
                                                aria-sort={orden === clave ? (sentido === 'desc' ? 'descending' : 'ascending') : undefined}
                                                className={`${CELDA} text-left text-[11px] font-bold tracking-wider text-[#57544E] whitespace-nowrap`}>
                                                <Titulo clave={clave} texto={texto} orden={orden} sentido={sentido}
                                                    onOrdenar={ordenar} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                {deSuscripciones ? filas.map((f) => <FilaDeSuscripcion key={f.clave} f={f} />) : filas.map((f) => (
                                    <Fila key={f.clave} f={f} abierto={!!abiertos[f.clave]} onToggle={() => alternar(f.clave)}
                                        entrenador={f.entrenadorId ? nombreDeEntrenador(entrenadores, f.entrenadorId) : null}
                                        puedeQuedarselo={!!f.id && accionDeEntrenador({ rol: user?.role, miId: user?.id,
                                            entrenadorId: f.entrenadorId }) === 'asignarmelo'}
                                        onQuedarselo={() => quedarselo(f)} />
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

        </div>
    );
}
